import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
  Chip,
  Alert,
  Grid,
  Stack,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Schedule,
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { useGuestContext } from '../../context/GuestContext';
import {
  getGuestAssessmentsByTopic,
  startGuestAssessment,
  submitGuestAssessmentAttempt,
  type AssessmentDTO,
  type AssessmentAttemptDTO,
} from '../../services/guestApi';
import AssessmentRunner from './AssessmentRunner';
import type { Assessment, AssessmentAttempt } from '../../services/assessmentService';
import { format } from 'date-fns';

interface GuestAssessmentCardProps {
  topicId: number;
}

function mapToAssessment(d: AssessmentDTO): Assessment {
  return {
    id: d.id,
    title: d.title,
    description: d.description,
    courseId: d.courseId,
    createdBy: 0,
    creatorRole: '',
    scheduledDate: d.scheduledDate ?? '',
    dueDate: d.dueDate,
    durationMinutes: d.durationMinutes,
    passingScore: d.passingScore ?? 0,
    maxAttempts: d.maxAttempts ?? 1,
    requiresWorkSubmission: false,
    allowReassessment: false,
    status: d.status,
    topicIds: d.topicIds ?? [],
    questionCount: undefined,
    createdAt: '',
    updatedAt: '',
  };
}

function mapToAttempt(d: AssessmentAttemptDTO): AssessmentAttempt {
  return {
    id: d.id,
    assessmentId: d.assessmentId,
    assessmentTitle: d.assessmentTitle,
    studentId: 0,
    topicIds: d.topicIds ?? [],
    startedAt: d.startedAt,
    submittedAt: d.submittedAt,
    completedAt: d.submittedAt,
    score: d.score,
    pointsEarned: undefined,
    pointsTotal: undefined,
    correctAnswers: d.correctAnswers,
    totalQuestions: d.totalQuestions,
    timeTakenMinutes: d.timeTakenMinutes,
    status: d.status,
    passed: d.passed,
    workSubmitted: false,
    answers: d.answers,
    workFileUrls: undefined,
    gradingNotes: undefined,
    reassessmentAllowed: undefined,
    reassessmentGatedUntil: undefined,
  };
}

const GuestAssessmentCard: React.FC<GuestAssessmentCardProps> = ({ topicId }) => {
  const { guestId, isReady } = useGuestContext();
  const [loading, setLoading] = useState(true);
  const [assessments, setAssessments] = useState<AssessmentDTO[]>([]);
  const [activeAssessment, setActiveAssessment] = useState<Assessment | null>(null);
  const [activeAttempt, setActiveAttempt] = useState<AssessmentAttempt | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isReady || !guestId) return;
    loadAssessments();
  }, [isReady, guestId, topicId]);

  const loadAssessments = async () => {
    if (!guestId) return;
    setLoading(true);
    setError(null);
    try {
      const list = await getGuestAssessmentsByTopic(guestId, topicId);
      setAssessments(list);
    } catch (_) {
      setError('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const handleStartAssessment = async (assessment: AssessmentDTO) => {
    if (!guestId) return;
    try {
      const attempt = await startGuestAssessment(guestId, assessment.id);
      setActiveAssessment(mapToAssessment(assessment));
      setActiveAttempt(mapToAttempt(attempt));
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to start assessment');
    }
  };

  const handleCloseAssessment = () => {
    setActiveAssessment(null);
    setActiveAttempt(null);
    loadAssessments();
  };

  const submitSubmission = async (attemptId: number, submission: { answers?: Record<string, unknown>; workFileUrls?: string[] }) => {
    if (!guestId) throw new Error('Guest session required');
    const result = await submitGuestAssessmentAttempt(guestId, attemptId, {
      answers: submission.answers,
      workFileUrls: submission.workFileUrls,
    });
    return mapToAttempt(result);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
    );
  }

  if (assessments.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Assessments
          </Typography>
          <Alert severity="info">No assessments available for this topic. (Guest: max 1 attempt total.)</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Assessments
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Complete an assessment for this topic (guest: 1 attempt total).
          </Typography>
          <Grid container spacing={2}>
            {assessments.map((assessment) => (
              <Grid item xs={12} sm={6} md={4} key={assessment.id}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>{assessment.title}</Typography>
                    {assessment.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {assessment.description}
                      </Typography>
                    )}
                    <Stack spacing={1} sx={{ mb: 2 }}>
                      {assessment.scheduledDate && (
                        <Chip
                          icon={<Schedule />}
                          label={`Due: ${format(new Date(assessment.scheduledDate), 'MMM dd, yyyy')}`}
                          size="small"
                        />
                      )}
                      {assessment.durationMinutes && (
                        <Chip label={`${assessment.durationMinutes} min`} size="small" />
                      )}
                      {assessment.passingScore != null && (
                        <Chip label={`Pass: ${assessment.passingScore}%`} size="small" color="success" />
                      )}
                    </Stack>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => handleStartAssessment(assessment)}
                    >
                      Start Assessment
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {activeAssessment && activeAttempt && (
        <Dialog open fullScreen onClose={handleCloseAssessment}>
          <DialogContent sx={{ p: 0, position: 'relative' }}>
            <IconButton
              onClick={handleCloseAssessment}
              sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}
            >
              <CloseIcon />
            </IconButton>
            <AssessmentRunner
              assessment={activeAssessment}
              attempt={activeAttempt}
              onComplete={handleCloseAssessment}
              onSubmitSubmission={submitSubmission}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default GuestAssessmentCard;
